import React, { useState } from 'react';
import { SlideExportButton } from '../components/slides/SlideExportButton';
import { SlidePreviewDialog } from '../components/slides/SlidePreviewDialog';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CommentIcon from '@mui/icons-material/Comment';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { lessonService } from '../services/lessonService';
import { reviewService } from '../services/reviewService';
import { useAuthStore } from '../store/authStore';
import type { Comment } from '../types';

export const LessonReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMarkdown, setPreviewMarkdown] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: lessonData, isLoading: isLessonLoading } = useQuery({
    queryKey: ['lesson', id],
    queryFn: () => lessonService.getById(id!),
    enabled: !!id,
  });

  const lesson = lessonData?.lesson;
  const version = lessonData?.version;
  const versionNumber = version?.version_number ?? 1;

  // Collect all slides across all stages
  const allSlides: Array<{ stageName: string; slide: { html: string; slide_number: number } }> = [];
  if (version?.slides_html) {
    Object.entries(version.slides_html).forEach(([stageName, slides]) => {
      slides.forEach((slide) => {
        allSlides.push({ stageName, slide });
      });
    });
  }

  const currentSlide = allSlides[currentSlideIndex] ?? null;

  const { data: comments = [], isLoading: isCommentsLoading } = useQuery({
    queryKey: ['comments', id, versionNumber],
    queryFn: () => reviewService.getComments(id!, versionNumber),
    enabled: !!id,
  });

  const addCommentMutation = useMutation({
    mutationFn: () =>
      reviewService.addComment(id!, versionNumber, {
        comment_text: commentText,
        slide_number: currentSlide?.slide.slide_number,
      }),
    onSuccess: () => {
      setCommentText('');
      queryClient.invalidateQueries({ queryKey: ['comments', id, versionNumber] });
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => reviewService.approve(id!, versionNumber),
    onSuccess: () => {
      setApproveOpen(false);
      queryClient.invalidateQueries({ queryKey: ['lesson', id] });
      navigate('/reviews');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => reviewService.reject(id!, versionNumber, rejectReason),
    onSuccess: () => {
      setRejectOpen(false);
      queryClient.invalidateQueries({ queryKey: ['lesson', id] });
      navigate('/reviews');
    },
  });

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      setRejectReasonError('Rejection reason is required.');
      return;
    }
    setRejectReasonError('');
    rejectMutation.mutate();
  };

  const commentsForCurrentSlide = comments.filter(
    (c) => c.slide_number === currentSlide?.slide.slide_number
  );
  const generalComments = comments.filter((c) => c.slide_number === undefined || c.slide_number === null);

  const canReview = user?.role === 'admin' || user?.role === 'content_manager';
  const isAlreadyReviewed =
    version?.approved_at || version?.rejection_reason;

  if (isLessonLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={64} sx={{ mb: 2, borderRadius: 2 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" sx={{ flex: 1, height: 500, borderRadius: 2 }} />
          <Skeleton variant="rectangular" sx={{ width: 340, height: 500, borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {lesson?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lesson?.cefr_level} &bull; {lesson?.topic} &bull; Author:{' '}
            {lesson?.author?.name ?? lesson?.author_id}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label={lesson?.status?.replace('_', ' ') ?? ''} />
          {id && versionNumber && (
            <Box sx={{ display: 'none' }}>
              <SlideExportButton
                lessonId={id}
                versionNumber={versionNumber}
                onHTMLReady={(html, markdown) => {
                  setPreviewHtml(html);
                  setPreviewMarkdown(markdown);
                  setPreviewOpen(true);
                }}
              />
            </Box>
          )}
          <Button variant="outlined" onClick={() => navigate('/reviews')}>
            Back
          </Button>
        </Box>
      </Box>

      {isAlreadyReviewed && (
        <Alert severity={version?.approved_at ? 'success' : 'warning'}>
          {version?.approved_at
            ? `Approved on ${new Date(version.approved_at).toLocaleDateString()}`
            : `Rejected: ${version?.rejection_reason}`}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
        {/* Left: Slide preview */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper
            elevation={2}
            sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#1a1a1a', aspectRatio: '16/9' }}
          >
            {allSlides.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'grey.500',
                }}
              >
                <Typography>No slides generated</Typography>
              </Box>
            ) : (
              <iframe
                title="Slide Preview"
                srcDoc={currentSlide?.slide.html ?? ''}
                sandbox="allow-scripts"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </Paper>

          {/* Navigation */}
          {allSlides.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <IconButton
                onClick={() => setCurrentSlideIndex((i) => Math.max(0, i - 1))}
                disabled={currentSlideIndex === 0}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" fontWeight={600}>
                  Slide {currentSlideIndex + 1} of {allSlides.length}
                </Typography>
                {currentSlide && (
                  <Typography variant="caption" color="text.secondary">
                    Stage: {currentSlide.stageName}
                  </Typography>
                )}
              </Box>
              <IconButton
                onClick={() =>
                  setCurrentSlideIndex((i) => Math.min(allSlides.length - 1, i + 1))
                }
                disabled={currentSlideIndex === allSlides.length - 1}
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          )}

          {/* Approve/Reject buttons */}
          {canReview && !isAlreadyReviewed && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setApproveOpen(true)}
                sx={{ px: 4 }}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setRejectOpen(true)}
                sx={{ px: 4 }}
              >
                Reject
              </Button>
            </Box>
          )}
        </Box>

        {/* Right: Comments panel */}
        <Paper
          sx={{
            width: 340,
            flexShrink: 0,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CommentIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={600}>
                Comments ({comments.length})
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {isCommentsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                {currentSlide && commentsForCurrentSlide.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="primary" fontWeight={600}>
                      Slide {currentSlide.slide.slide_number} Comments
                    </Typography>
                    {commentsForCurrentSlide.map((c) => (
                      <CommentItem key={c.id} comment={c} />
                    ))}
                  </Box>
                )}

                {generalComments.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      General Comments
                    </Typography>
                    {generalComments.map((c) => (
                      <CommentItem key={c.id} comment={c} />
                    ))}
                  </Box>
                )}

                {comments.length === 0 && (
                  <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 4 }}>
                    No comments yet
                  </Typography>
                )}
              </>
            )}
          </Box>

          {canReview && (
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={
                  currentSlide
                    ? `Comment on slide ${currentSlide.slide.slide_number}...`
                    : 'Add a general comment...'
                }
              />
              <Button
                fullWidth
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
                onClick={() => addCommentMutation.mutate()}
                disabled={!commentText.trim() || addCommentMutation.isPending}
              >
                {addCommentMutation.isPending ? <CircularProgress size={16} /> : 'Add Comment'}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)}>
        <DialogTitle>Approve Lesson?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark the lesson as approved and it will be ready to publish.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => approveMutation.mutate()}
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending ? <CircularProgress size={20} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Lesson</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a reason for rejection. This will be shown to the author.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => { setRejectReason(e.target.value); setRejectReasonError(''); }}
            error={!!rejectReasonError}
            helperText={rejectReasonError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRejectSubmit}
            disabled={rejectMutation.isPending}
          >
            {rejectMutation.isPending ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Slide preview dialog */}
      {id && versionNumber && previewOpen && (
        <SlidePreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          html={previewHtml}
          markdown={previewMarkdown}
          lessonId={id}
          versionNumber={versionNumber}
          onMarkdownUpdated={(md, html) => {
            setPreviewMarkdown(md);
            setPreviewHtml(html);
          }}
        />
      )}
    </Box>
  );
};

const CommentItem: React.FC<{ comment: Comment }> = ({ comment }) => (
  <Card sx={{ mb: 1, mt: 0.5 }} variant="outlined">
    <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="caption" fontWeight={600} color="primary">
          {comment.reviewer?.name ?? comment.reviewer_id}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(comment.created_at).toLocaleDateString()}
        </Typography>
      </Box>
      <Typography variant="body2">{comment.comment_text}</Typography>
    </CardContent>
  </Card>
);
